import { useMutation, useQuery } from "@tanstack/react-query";
import {
  Button,
  Divider,
  Form,
  Input,
  message,
  Space,
  theme,
  Typography,
} from "antd";
import { get, pick, set } from "lodash";
import { useCallback, useEffect, useRef } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import axiosInstance, { endpoints } from "services/axios";
import Holidays from "./components/holidays";
import WorkingHours from "./components/working-hours";
import moment from "moment";
import BreakPenalty from "./components/break-penalty";
import useNavigateWithParams from "hooks/use-navigate-with-params";
import CLevel from "./components/c-level";

interface HolidaysRef {
  getData: () => string[];
}

const SchedulePage = () => {
  const params = useParams();
  const [searchParams] = useSearchParams();
  const accessToken = searchParams.get("token");
  const folderId = searchParams.get("folderId") || null;
  const holidaysRef = useRef<HolidaysRef>(null);
  const navigate = useNavigateWithParams();

  const [form] = Form.useForm();
  const { token } = theme.useToken();

  const { data: schedule_data } = useQuery({
    queryKey: ["schedule", params.id],
    queryFn: async () => {
      axiosInstance.defaults.headers.common.Authorization = accessToken;
      return await axiosInstance.get(
        endpoints.schedule.one(params.id as string)
      );
    },
    enabled: !!params.id,
  });

  const scheduleData = get(schedule_data, "data.data", {});

  const { mutate: createOrEditSchedule, isPending: submitting } = useMutation({
    mutationFn: async (data: any) => {
      set(data, "folderId", folderId);
      if (params.id) {
        return await axiosInstance.put(
          endpoints.schedule.update(params.id),
          data
        );
      } else {
        return await axiosInstance.post(endpoints.schedule.add, data);
      }
    },
    onSuccess: ({ data }) => {
      message.success(
        params.id
          ? "Schedule updated successfully!"
          : "Schedule created successfully!"
      );
      if (!params.id) {
        navigate(`/schedule/${get(data, "data.id")}`);
      }
    },
  });

  const changeFormatDaysToSubmitData = useCallback(
    (daysValues: Record<string, any>) => {
      const days = [];

      for (const weekDayName in daysValues) {
        const day = daysValues[weekDayName];
        if (day.selected) {
          const enterTime = get(day, "workingHours", "")
            ?.split("-")
            .map((t: string) =>
              t
                .split(":")
                .reduce(
                  (sum: any, val: any, i: any) => sum + val * [3600, 60, 1][i],
                  0
                )
            )[0];
          const exitTime = get(day, "workingHours", "")
            ?.split("-")
            .map((t: string) =>
              t
                .split(":")
                .reduce(
                  (sum: any, val: any, i: any) => sum + val * [3600, 60, 1][i],
                  0
                )
            )[1];

          const breakStartTime = get(day, "breakingHours", "")
            ?.split("-")
            .map((t: string) =>
              t
                .split(":")
                .reduce(
                  (sum: any, val: any, i: any) => sum + val * [3600, 60, 1][i],
                  0
                )
            )[0];
          const breakEndTime = get(day, "breakingHours", "")
            ?.split("-")
            .map((t: string) =>
              t
                .split(":")
                .reduce(
                  (sum: any, val: any, i: any) => sum + val * [3600, 60, 1][i],
                  0
                )
            )[1];

          days.push({
            id: day.id,
            day: weekDayName,
            workingHour: get(day, "workingHour", "")
              .split(":")
              .reduce(
                (sum: any, val: any, i: any) => sum + val * [3600, 60, 1][i],
                0
              ),
            enterTime,
            exitTime,
            breakStartTime,
            breakEndTime,
          });
        }
      }

      return days;
    },
    []
  );

  function secondsToHHMM(seconds: number) {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);

    // Pad with leading zeros if needed
    const hh = String(hours).padStart(2, "0");
    const mm = String(minutes).padStart(2, "0");

    return seconds ? `${hh}:${mm}` : "";
  }

  const convertMomentRangeToString = (
    times?: [moment.Moment, moment.Moment] | null
  ): string => {
    if (!times || times.length !== 2) return "";
    const [start, end] = times;
    return `${start.format("HH:mm:ss")} - ${end.format("HH:mm:ss")}`;
  };

  const changeFormatDaysToFormData = useCallback((days: any[]) => {
    const daysValues: any = {};

    days.forEach((dayData) => {
      const workingHour = secondsToHHMM(dayData.workingHour);

      const workingHours = convertMomentRangeToString([
        moment().startOf("day").add(dayData.enterTime, "seconds"),
        moment().startOf("day").add(dayData.exitTime, "seconds"),
      ]);
      const breakingHours =
        dayData.breakStartTime != null && dayData.breakEndTime != null
          ? convertMomentRangeToString([
              moment().startOf("day").add(dayData.breakStartTime, "seconds"),
              moment().startOf("day").add(dayData.breakEndTime, "seconds"),
            ])
          : "";

      daysValues[dayData.day] = {
        selected: true,
        id: dayData.id,
        workingHour,
        workingHours,
        breakingHours,
      };
    });

    return daysValues;
  }, []);

  const onFinish = useCallback(
    (formData: any) => {
      const submitData: any = pick(formData, [
        "name",
        "penaltyOverBreakCount",
        "breakMinutes",
        "forCLevel",
        "cLevelAllowedHours",
      ]);
      submitData.days = changeFormatDaysToSubmitData(formData.days);
      submitData.holidays = holidaysRef.current
        ? holidaysRef.current.getData()
        : null;
      submitData.penaltyForOverBreak = !!formData.penaltyForOverBreak;
      submitData.forCLevel = submitData.forCLevel ?? false;

      createOrEditSchedule(submitData);
    },
    [changeFormatDaysToSubmitData, createOrEditSchedule]
  );

  useEffect(() => {
    if (scheduleData) {
      const formData: any = pick(scheduleData, [
        "name",
        "penaltyForOverBreak",
        "penaltyOverBreakCount",
        "breakMinutes",
        "forCLevel",
        "cLevelAllowedHours",
      ]);
      formData.days = changeFormatDaysToFormData(get(scheduleData, "days", []));
      form.setFieldsValue(formData);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scheduleData]);

  return (
    <div>
      <Typography.Title
        className="grid flex-1 gap-2"
        level={4}
        style={{ gridTemplateColumns: "max-content max-content auto" }}
      >
        <span
          className="cursor-pointer"
          style={{ color: token.colorSuccess }}
          onClick={() => navigate("/")}
        >
          Schedules
        </span>
        <span>/</span>
        <span className="inline-block text-ellipsis overflow-hidden text-nowrap">
          {params.id ? "Edit schedule" : "Create schedule"}
        </span>
      </Typography.Title>

      <Form
        layout="vertical"
        form={form}
        name="schedule_form"
        className="mt-10"
        onFinish={onFinish}
        autoComplete="off"
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            e.preventDefault();
          }
        }}
      >
        <Form.Item
          name={"name"}
          label="Schedule name"
          rules={[{ required: true }]}
          required
        >
          <Input />
        </Form.Item>

        <Divider />

        <Typography.Title level={4} className="mb-5">
          Working Hours
        </Typography.Title>

        <WorkingHours form={form} />

        <Divider />

        <BreakPenalty form={form} />
        <CLevel form={form} />
        <Divider />

        <Typography.Title level={4} className="mb-5">
          Holidays
        </Typography.Title>

        <Holidays ref={holidaysRef} scheduleData={scheduleData} />

        <Space className="mb-28 mt-5">
          <Button onClick={() => navigate("/")} type="dashed">
            Cancel
          </Button>
          <Button loading={submitting} htmlType="submit" type="primary">
            {params.id ? "Update" : "Create"}
          </Button>
        </Space>
      </Form>
    </div>
  );
};

export default SchedulePage;
